import React, { useState } from "react";
import { useGroup } from "@/context/GroupContext";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Check, Users2, Plus, Shield, Sparkles } from "lucide-react";
import UpgradeDialog from "@/components/UpgradeDialog";

export default function Groups() {
  const { myGroups, slug: activeSlug } = useGroup();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  const isFree = user?.plan !== "pro" && user?.role !== "superadmin";
  // count groups where user is admin
  const adminOf = myGroups.filter((g) => g.is_admin).length;
  const atLimit = isFree && adminOf >= 1;

  const createNew = () => {
    if (atLimit) setUpgradeOpen(true);
    else navigate("/new");
  };

  return (
    <div className="space-y-5 lg:max-w-2xl">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Switch</div>
          <h1 className="heading text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 mt-1">Your groups</h1>
        </div>
        <button
          onClick={createNew}
          className="flex items-center gap-2 px-4 h-11 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold active:scale-95"
          data-testid="groups-create-new"
        >
          <Plus className="w-4 h-4" /> New
        </button>
      </div>

      {isFree && (
        <div className="p-4 rounded-2xl border border-emerald-200 bg-emerald-50 flex items-center gap-3" data-testid="groups-plan-banner">
          <div className="w-10 h-10 rounded-2xl bg-emerald-600 grid place-items-center text-white">
            <Sparkles className="w-4 h-4" />
          </div>
          <div className="flex-1">
            <div className="text-sm font-semibold text-slate-900">Free plan</div>
            <div className="text-xs text-slate-600 mt-0.5">
              You can admin 1 group on Free. Upgrade to Pro for unlimited groups.
            </div>
          </div>
          <button
            onClick={() => setUpgradeOpen(true)}
            className="text-xs font-semibold px-3 h-9 rounded-full bg-slate-900 text-white active:scale-95"
            data-testid="banner-upgrade-button"
          >
            Upgrade
          </button>
        </div>
      )}

      {myGroups.length === 0 && (
        <div className="text-center py-10">
          <Users2 className="w-10 h-10 mx-auto text-slate-300" />
          <p className="text-slate-500 text-sm mt-3">You don't belong to any group yet.</p>
          <button
            onClick={() => navigate("/new")}
            className="mt-4 h-11 px-5 rounded-full bg-slate-900 text-white text-sm font-semibold"
          >
            Create your first group
          </button>
        </div>
      )}

      <div className="space-y-3">
        {myGroups.map((g) => (
          <button
            key={g.id}
            onClick={() => navigate(`/${g.slug}`)}
            className={`w-full text-left flex items-center gap-3 p-4 rounded-2xl border transition-all ${
              g.slug === activeSlug ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 hover:border-slate-300"
            }`}
            data-testid={`group-${g.slug}`}
          >
            <div className={`w-11 h-11 rounded-2xl grid place-items-center heading font-semibold ${
              g.slug === activeSlug ? "bg-white/10 text-white" : "bg-slate-900 text-white"
            }`}>
              {g.name[0]}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <div className="font-semibold truncate">{g.name}</div>
                {g.is_admin && (
                  <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full font-bold flex items-center gap-1 ${
                    g.slug === activeSlug ? "bg-white/15 text-white" : "bg-emerald-100 text-emerald-700"
                  }`}>
                    <Shield className="w-2.5 h-2.5" /> Admin
                  </span>
                )}
              </div>
              <div className={`text-xs font-mono mt-0.5 ${g.slug === activeSlug ? "text-white/70" : "text-slate-500"}`}>
                /{g.slug} · {g.member_ids?.length || 0} members
              </div>
            </div>
            {g.slug === activeSlug && <Check className="w-5 h-5" />}
          </button>
        ))}
      </div>
      <UpgradeDialog open={upgradeOpen} onOpenChange={setUpgradeOpen} onUpgraded={() => window.location.reload()} />
    </div>
  );
}
